import { useState } from 'react';

const partnerTypes = [
  {
    icon: 'ri-user-heart-line',
    label: 'Life & Business Coaches',
  },
  {
    icon: 'ri-mental-health-line',
    label: 'Therapeuten & Berater',
  },
  {
    icon: 'ri-building-2-line',
    label: 'Unternehmen & HR',
  },
  {
    icon: 'ri-hospital-line',
    label: 'Kliniken & Reha-Zentren',
  },
  {
    icon: 'ri-graduation-cap-line',
    label: 'Bildungseinrichtungen',
  },
  {
    icon: 'ri-hand-heart-line',
    label: 'NGOs & Soziale Träger',
  },
];

type FormState = 'idle' | 'loading' | 'success' | 'error';

export default function Partnership() {
  const [activeTab, setActiveTab] = useState<'kooperation' | 'whitelabel'>('kooperation');
  const [formState, setFormState] = useState<FormState>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    interest: 'kooperation',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');

    const body = new URLSearchParams();
    Object.entries(formData).forEach(([key, val]) => body.append(key, val));
    body.set('interest', activeTab);

    try {
      const res = await fetch('https://readdy.ai/api/form/d734gj9ttkj2t7sc9is0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setFormState('success');
        setFormData({ name: '', email: '', organization: '', interest: 'kooperation', message: '' });
      } else {
        setFormState('error');
      }
    } catch {
      setFormState('error');
    }
  };

  return (
    <section className="py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-teal-900 relative overflow-hidden">
      {/* Decorative orbs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 text-teal-300 px-5 py-2 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
            <i className="ri-handshake-line text-base"></i>
            Gemeinsam wachsen
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Kooperationen &amp; <span className="text-teal-400">Whitelabel</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Mindshift AI als eigene Marke, eigenes Tool – für Coaches, Unternehmen und Institutionen, die ihren Klienten echten Mehrwert bieten wollen.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Offer Cards + Target Groups */}
          <div className="space-y-6">
            {/* Tab Switch */}
            <div className="flex bg-white/10 backdrop-blur-sm rounded-full p-1 border border-white/10">
              <button
                onClick={() => setActiveTab('kooperation')}
                className={`flex-1 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'kooperation'
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <i className="ri-handshake-line mr-2"></i>
                Kooperation / Partner
              </button>
              <button
                onClick={() => setActiveTab('whitelabel')}
                className={`flex-1 py-2.5 px-4 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'whitelabel'
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-300 hover:text-white'
                }`}
              >
                <i className="ri-store-2-line mr-2"></i>
                Whitelabel
              </button>
            </div>

            {/* Content Cards */}
            {activeTab === 'kooperation' ? (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 space-y-5">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-xl">
                    <i className="ri-handshake-line text-xl text-teal-400"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white">Kooperation &amp; Partnerschaft</h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Empfehle Mindshift AI deinen Klienten und erhalte attraktive Provisionen. Wir unterstützen dich mit Materialien, Co-Marketing und einem direkten Ansprechpartner.
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: 'ri-percent-line', text: 'Attraktive Affiliate-Provisionen' },
                    { icon: 'ri-megaphone-line', text: 'Co-Marketing & gemeinsame Kampagnen' },
                    { icon: 'ri-customer-service-2-line', text: 'Persönlicher Partner-Support' },
                    { icon: 'ri-gift-line', text: 'Exklusive Rabatte für deine Community' },
                    { icon: 'ri-file-text-line', text: 'Kostenlose Materialien & Ressourcen' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-200 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-teal-400`}></i>
                      </div>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-7 space-y-5">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-500/20 rounded-xl">
                    <i className="ri-store-2-line text-xl text-teal-400"></i>
                  </div>
                  <h3 className="text-xl font-bold text-white">Whitelabel – Deine Marke</h3>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  Nutze die komplette Mindshift AI Plattform unter deiner eigenen Marke. Eigenes Logo, eigene Domain, eigene Farben – vollständig angepasst an dein Erscheinungsbild.
                </p>
                <ul className="space-y-3">
                  {[
                    { icon: 'ri-palette-line', text: 'Vollständiges Branding nach deinen Vorgaben' },
                    { icon: 'ri-global-line', text: 'Eigene Domain & eigene App' },
                    { icon: 'ri-settings-4-line', text: 'Individuell konfigurierbare Features' },
                    { icon: 'ri-shield-check-line', text: 'DSGVO-konform, in Deutschland gehostet' },
                    { icon: 'ri-customer-service-2-line', text: 'Dediziertes Onboarding & Support-Team' },
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-200 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-teal-400`}></i>
                      </div>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Partner Types */}
            <div>
              <p className="text-slate-400 text-sm font-semibold uppercase tracking-widest mb-4">Passend für</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {partnerTypes.map((pt, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5"
                  >
                    <div className="w-6 h-6 flex items-center justify-center shrink-0">
                      <i className={`${pt.icon} text-teal-400 text-sm`}></i>
                    </div>
                    <span className="text-slate-300 text-xs leading-tight">{pt.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-2">Jetzt Anfrage stellen</h3>
            <p className="text-slate-400 text-sm mb-7">
              Wir melden uns innerhalb von 24 Stunden bei dir. Unverbindlich und kostenlos.
            </p>

            {formState === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-500/20 rounded-full mx-auto mb-5">
                  <i className="ri-checkbox-circle-line text-4xl text-teal-400"></i>
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Anfrage gesendet!</h4>
                <p className="text-slate-300">
                  Vielen Dank – wir melden uns bald bei dir. Wir freuen uns auf die Zusammenarbeit!
                </p>
                <button
                  onClick={() => setFormState('idle')}
                  className="mt-6 text-teal-400 hover:text-teal-300 text-sm underline cursor-pointer"
                >
                  Weitere Anfrage senden
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-1.5">Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="Dein Name"
                      value={formData.name}
                      onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 text-sm font-medium mb-1.5">E-Mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="deine@email.de"
                      value={formData.email}
                      onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-1.5">Organisation / Unternehmen</label>
                  <input
                    type="text"
                    name="organization"
                    placeholder="Dein Unternehmen, Praxis oder Organisation"
                    value={formData.organization}
                    onChange={e => setFormData(p => ({ ...p, organization: e.target.value }))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-1.5">Ich interessiere mich für *</label>
                  <select
                    name="interest"
                    value={activeTab}
                    onChange={e => setActiveTab(e.target.value as 'kooperation' | 'whitelabel')}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-teal-400 transition-colors cursor-pointer"
                  >
                    <option value="kooperation" className="bg-slate-800">Kooperation / Partnerschaft</option>
                    <option value="whitelabel" className="bg-slate-800">Whitelabel-Lösung</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-1.5">Deine Nachricht</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Erzähl uns kurz von dir und deinen Vorstellungen..."
                    value={formData.message}
                    onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-400 transition-colors resize-none"
                  />
                  <p className="text-slate-500 text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>

                {formState === 'error' && (
                  <p className="text-red-400 text-sm">
                    Etwas ist schiefgelaufen. Bitte versuche es erneut oder schreib uns direkt an.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formState === 'loading'}
                  className="w-full bg-teal-500 hover:bg-teal-400 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all duration-300 hover:scale-[1.02] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {formState === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Unverbindlich anfragen
                    </>
                  )}
                </button>

                <p className="text-slate-500 text-xs text-center">
                  Mit dem Absenden stimmst du unserer{' '}
                  <a href="/datenschutz" className="text-teal-400 hover:underline">Datenschutzerklärung</a>{' '}
                  zu.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
